/*
 * Support / Contact page.
 * Answers must match how the shipping app behaves; check before editing.
 */
import { useState } from "react";
import { ChevronDown, Facebook, Instagram, Mail } from "lucide-react";
import { Link } from "wouter";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FACEBOOK_URL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from "@/lib/socialLinks";

const FAQ = [
  {
    q: "How do I request a ride?",
    a: "Open H-Plate, search for where you're going and confirm the fare. Your request goes to available drivers nearby, and the app shows you when one accepts and where the bus is.",
  },
  {
    q: "How much does a ride cost?",
    a: "Fares are set by the Government of Dominica and shown in EC$ before you confirm. There is no surge pricing and no booking fee.",
  },
  {
    q: "How do I pay?",
    a: "Pay the driver in cash at the end of the ride, the same as any H-bus. H-Plate itself is free.",
  },
  {
    q: "I drive an H-bus. How do I use H-Plate?",
    a: "H-Plate doesn't employ drivers: you run your own bus and use the app to see riders on your route. Download H-Plate, sign up and choose Driver. Add your bus (plate, make, model, colour and seats) and upload your driver's licence and a selfie. You need a valid H-plate vehicle registration from the Government of Dominica.",
  },
  {
    q: "What if I feel unsafe during a ride?",
    a: "Tap SOS in the app. It logs your GPS position, sends a text to your emergency contact and sounds an alarm on the driver's phone. In a life-threatening emergency, always call 911 first.",
  },
  {
    q: "Can my family follow my trip?",
    a: "Yes. During a ride, tap Share trip and send the link. It shows the bus on a map without your name, and stops working when the trip ends.",
  },
  {
    q: "Can I use H-Plate with poor signal?",
    a: "Drivers can accept, pick up and complete rides when signal drops; the app syncs when it reconnects. Riders need a connection to request a ride.",
  },
  {
    q: "How do I delete my account?",
    a: "In the app, tap Profile, then your name at the top, then Delete account. Or email support@h-plate.com and we'll process it within 5 working days.",
    link: { href: "/delete-account", label: "Full details" },
  },
];

/**
 * Public support page with contact details and common questions.
 */
export default function Support() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1 pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="container grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-emerald-700">Support</p>
            <h1 className="mt-3 font-display text-[44px] leading-[1.05] md:text-6xl tracking-tight text-slate-900">
              How can we help?
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Whether you ride or drive, we're here to help. Most answers are
              below. For anything else, email us.
            </p>

            <div className="mt-10 rounded-2xl border border-slate-200 p-7">
              <p className="text-sm font-semibold text-slate-500">Email</p>
              <a
                href="mailto:support@h-plate.com"
                className="mt-1 inline-flex items-center gap-2 font-display text-2xl text-emerald-800 no-underline hover:text-emerald-900"
              >
                support@h-plate.com
              </a>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                For account issues, ride problems, data requests or general
                questions. We reply within 5 working days.
              </p>
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-500">In an emergency</p>
                <p className="mt-1 text-[15px] leading-relaxed text-slate-700">
                  Call <span className="font-semibold">911</span>, or the police on (767) 266 5100. Email is not
                  monitored around the clock.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-500">Follow us</p>
                <p className="mt-1 text-[15px] leading-relaxed text-slate-700">
                  Route news, service updates and tips.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 px-4 text-[15px] font-medium text-slate-800 no-underline hover:border-emerald-700 hover:text-emerald-800 transition-colors"
                  >
                    <Instagram className="h-4 w-4" aria-hidden="true" />
                    Instagram {INSTAGRAM_HANDLE}
                  </a>
                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 px-4 text-[15px] font-medium text-slate-800 no-underline hover:border-emerald-700 hover:text-emerald-800 transition-colors"
                  >
                    <Facebook className="h-4 w-4" aria-hidden="true" />
                    Facebook
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-sans text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
              Common questions
            </h2>
            <div className="mt-4 border-t border-slate-200">
              {FAQ.map((item, i) => {
                const open = openFaq === i;
                return (
                  <div key={item.q} className="border-b border-slate-200">
                    <button
                      className="w-full flex items-center justify-between gap-6 py-5 text-left bg-transparent border-0 cursor-pointer"
                      onClick={() => setOpenFaq(open ? null : i)}
                      aria-expanded={open}
                    >
                      <span className="text-lg font-medium text-slate-900">{item.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    {open && (
                      <div className="pb-6 pr-10">
                        <p className="text-[15px] leading-relaxed text-slate-600">
                          {item.a}
                          {item.link && (
                            <>
                              {" "}
                              <Link href={item.link.href} className="font-semibold text-emerald-800 hover:text-emerald-900">
                                {item.link.label}
                              </Link>
                              .
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="mailto:support@h-plate.com"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-emerald-700 text-white font-semibold no-underline hover:bg-emerald-800 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email support
              </a>
              <span className="text-[15px] text-slate-500">Can't find your answer? Write to us.</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
